import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { wibNow, wibDateStr } from "@/lib/time";
import { PageHeader } from "@/components/PageHeader";
import { KidAvatar } from "@/components/KidAvatar";

const MON = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const MON_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

function StatusBadge({ status }) {
  const map = {
    Hadir: "bg-green-100 text-green-700",
    Terlambat: "bg-amber-100 text-amber-700",
    Izin: "bg-sky-100 text-sky-700",
    "Tidak Hadir": "bg-red-100 text-red-600",
  };
  return <span data-testid="detail-status-badge" className={`text-[11px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${map[status] || "bg-stone-100 text-stone-500"}`}>{status}</span>;
}

export default function AdminGuruDetail() {
  const { teacherId } = useParams();
  const now = wibNow();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year] = useState(now.getFullYear());
  const [guru, setGuru] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setError(false);
    api.get(`/teacher/attendance/report?month=${month}&year=${year}&teacher_id=${teacherId}`)
      .then((r) => setGuru(r.data[0] || null))
      .catch(() => setError(true));
  }, [teacherId, month, year]);

  // build full month rows: one row per elapsed workday + any izin/records; newest first
  const rows = [];
  if (guru) {
    const todayStr = wibDateStr();
    const recMap = {};
    guru.records.forEach((r) => { recMap[r.date] = r; });
    for (let d = 1; d <= guru.last_day; d++) {
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const dow = new Date(`${dateStr}T00:00:00`).getDay();
      const rec = recMap[dateStr];
      if (rec) {
        rows.push({ date: dateStr, d, status: rec.status, time_in: rec.time_in, time_out: rec.time_out, reason: rec.reason });
        continue;
      }
      if (dateStr > todayStr) continue;      // future day, not yet elapsed
      if (dow === 0 || dow === 6) continue;  // skip empty weekends
      rows.push({ date: dateStr, d, status: "Tidak Hadir", time_in: null, time_out: null, reason: null });
    }
    rows.reverse();
  }

  return (
    <div>
      <PageHeader title="Detail Laporan" />
      <div className="px-5 pt-4 pb-6">
        {!guru ? (
          <p className="text-center text-stone-400 mt-10">{error ? "Gagal memuat data. Coba lagi." : "Memuat data..."}</p>
        ) : (
          <>
            {/* Profile card */}
            <div data-testid="detail-guru-card" className="bg-white rounded-3xl border border-stone-200/60 shadow-sm p-4 flex items-center gap-4">
              <KidAvatar gender="Perempuan" size={64} src={guru.avatar} />
              <div>
                <p className="font-heading font-bold text-lg text-stone-800">{guru.name}</p>
                <p className="text-stone-500 text-sm">{guru.code} • {guru.jabatan}</p>
              </div>
            </div>

            {/* Period + month filter */}
            <p className="text-sm text-stone-600 mt-4 mb-2">
              <span className="font-semibold">Periode:</span> 01 {MON_FULL[month - 1]} {year} – {guru.last_day} {MON_FULL[month - 1]} {year}
            </p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
              {MON.map((m, i) => (
                <button key={m} data-testid={`detail-bulan-${i + 1}`} onClick={() => setMonth(i + 1)}
                  className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-semibold ${month === i + 1 ? "bg-primary text-white" : "bg-white border border-stone-200 text-stone-500"}`}>{m}</button>
              ))}
            </div>

            {/* Recap table */}
            <div className="bg-white rounded-2xl border border-stone-200/60 shadow-sm overflow-hidden">
              <div className="grid grid-cols-[1fr_1fr_1fr_1.2fr] gap-1 px-3 py-2.5 bg-stone-50 border-b border-stone-100 text-[11px] font-bold text-stone-500">
                <span>Tanggal</span><span className="text-center">Masuk</span><span className="text-center">Pulang</span><span className="text-right">Status</span>
              </div>
              {rows.length === 0 && <p className="text-center text-stone-400 text-sm py-8">Belum ada rekaman bulan ini.</p>}
              {rows.map((r, i) => (
                <div key={r.date} data-testid={`detail-row-${i}`} className="border-b border-stone-50 last:border-0">
                  <div className="grid grid-cols-[1fr_1fr_1fr_1.2fr] gap-1 px-3 py-2.5 items-center text-sm">
                    <span className="text-stone-700 font-medium">{r.d} {MON[month - 1]}</span>
                    <span className="text-center text-stone-600">{r.time_in ? r.time_in.replace(".", ":") : "-"}</span>
                    <span className="text-center text-stone-600">{r.time_out ? r.time_out.replace(".", ":") : "-"}</span>
                    <span className="flex justify-end"><StatusBadge status={r.status} /></span>
                  </div>
                  {r.status === "Izin" && r.reason && (
                    <p data-testid={`detail-izin-reason-${i}`} className="px-3 pb-2.5 -mt-1 text-xs text-sky-600">Keterangan: {r.reason}</p>
                  )}
                </div>
              ))}
            </div>

            <button data-testid="detail-print" onClick={() => window.print()} className="no-print w-full bg-primary text-white font-semibold py-3.5 rounded-full mt-5 flex items-center justify-center gap-2 active:scale-95 transition-transform">
              <Printer size={18} weight="fill" /> Cetak / Export Laporan
            </button>
          </>
        )}
      </div>
    </div>
  );
}
